import { IonContent, IonHeader, IonPage, IonTitle, IonToolbar, IonCard, IonCardHeader, IonCardTitle, IonCardContent } from '@ionic/react';
import { IonSearchbar, IonBackButton, IonButtons, IonSpinner } from '@ionic/react';
import './customers.css';
import axios from "axios";
import getApi from '../api';
import EnvironmentBadge from '../components/EnvironmentBadge';
import React, { useState, useEffect } from 'react';

// Enklare och stabil useDebounce hook
function useDebounce(value: string, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

interface FortnoxCustomer {
  CustomerNumber: string;
  Name: string;
  OrganisationNumber: string;
  YourReference: string;
}

const Customers: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<FortnoxCustomer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // Använd vår egen debounce hook för sökningen
  const debouncedSearchQuery = useDebounce(searchQuery, 800);

  const api = getApi();

  // Hantera både initial data och sökningar (debounced)
  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    async function run() {
      setIsLoading(true);
      try {
        const url = debouncedSearchQuery.trim() === ''
          ? '/fortnox/customers/'
          : `/fortnox/customers/?search=${encodeURIComponent(debouncedSearchQuery.trim())}`;
        const res = await api.get(url, { signal: controller.signal });
        if (cancelled) return;
        const list = Array.isArray(res.data?.Customers) ? res.data.Customers : [];
        setResults(list);
      } catch (err: any) {
        if (cancelled) return;
        if (axios.isCancel(err)) {
          // request aborted, ignore
        } else if (err?.response?.status === 401) {
          console.warn('Unauthorized when loading customers');
        } else {
          console.error('Failed to load customers', err);
        }
        if (!cancelled) {
          // Keep previous results if searching, else reset on initial load
          if (debouncedSearchQuery === '') setResults([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [debouncedSearchQuery, api]);

    // Direkt-sök fallback (om vi vill trigga manuellt utan debounce). Använd annars bara sökfältet.
    const handleChange = (ev: Event) => {
      const target = ev.target as HTMLIonSearchbarElement;
      const raw = target?.value || '';
      setSearchQuery(raw); // låt debounce-effekten göra jobbet
    };

  return (
    <IonPage placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
      <IonHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <IonToolbar color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <IonButtons slot="start" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonBackButton />
        </IonButtons>
          <IonTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Kunder</IonTitle>
          <div slot="end" style={{paddingRight:'8px'}}><EnvironmentBadge /></div>
        </IonToolbar>
      </IonHeader>
      <IonContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
      <IonSearchbar
        placeholder="Sök Kund"
        value={searchQuery}
        onIonChange={e => setSearchQuery(e.detail.value || '')}
        debounce={800}
        animated={true}
        showCancelButton="focus"
        onPointerEnterCapture={undefined} 
        onPointerLeaveCapture={undefined}
      />
      
      {isLoading && (
        <div className="ion-text-center">
          <IonSpinner placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
        </div>
      )}

      <div>
  {results && results.map((result) => (
          <IonCard key={result.CustomerNumber} routerLink={"/Customer/" + result.CustomerNumber} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonCardHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonCardTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>{result.Name}</IonCardTitle>
            </IonCardHeader>
            <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <p>Kundnummer: {result.CustomerNumber}</p>
              <p>Org.nummer: {result.OrganisationNumber}</p>
              <p>Kontaktperson: {result.YourReference}</p>
            </IonCardContent>
          </IonCard>
        ))}
      </div>
      </IonContent>
    </IonPage>
  );
};

export default Customers;
